/**
 * useAccessMatrix — Single source of truth for the frontend authorization.
 *
 * Provides cached access to the user's effective modules + permissions +
 * tenant + plan. Replaces the scattered `if user.role === ...` checks.
 *
 * Strategy:
 *   - Loaded ONCE per session on mount (after auth ready)
 *   - Refreshed on demand via `reload()`
 *   - Available globally via React context — no prop drilling
 *
 * Usage:
 *   const { hasModule, hasPermission, isPlatform, loading } = useAccessMatrix();
 *   if (!hasModule("food_cost")) return <ModuleLocked module="food_cost" />;
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


const AccessMatrixContext = createContext(null);

const EMPTY = {
  effective_modules: [],
  plan_modules: [],
  overrides: {},
  permissions: [],
  tenant: null,
  plan: null,
  user: null,
};


export function AccessMatrixProvider({ children }) {
  const { user: authUser, checking } = useAuth();
  const [matrix, setMatrix] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!authUser) {
      setMatrix(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/access/matrix");
      setMatrix(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || "Access matrix load failed");
      setMatrix(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  // Load on user change
  useEffect(() => {
    if (checking) return;
    reload();
  }, [reload, checking]);

  const modulesSet = useMemo(
    () => new Set(matrix.effective_modules || []),
    [matrix.effective_modules]
  );
  const permsSet = useMemo(
    () => new Set(matrix.permissions || []),
    [matrix.permissions]
  );

  const value = useMemo(() => ({
    ...matrix,
    loading,
    error,
    reload,
    hasModule: (m) => modulesSet.has(m),
    hasAnyModule: (mods) => (mods || []).some((m) => modulesSet.has(m)),
    hasAllModules: (mods) => (mods || []).every((m) => modulesSet.has(m)),
    hasPermission: (p) => permsSet.has(p),
    isPlatform: !!matrix?.user?.is_platform,
  }), [matrix, loading, error, reload, modulesSet, permsSet]);

  return (
    <AccessMatrixContext.Provider value={value}>{children}</AccessMatrixContext.Provider>
  );
}


export function useAccessMatrix() {
  const ctx = useContext(AccessMatrixContext);
  if (!ctx) {
    throw new Error("useAccessMatrix must be used inside <AccessMatrixProvider>");
  }
  return ctx;
}
